<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import type { Project, ProjectCategory } from '../lib/types';
  import { categoryColors, categoryIcons } from '../lib/utils';
  import { draggedProject, openProjectRail } from '../lib/stores/ui';
  import { getTaskCounts, subscribeToProjectTaskCounts } from '../lib/useProjectTaskCounts';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    project: Project;
    compact?: boolean;
    clickable?: boolean;
  }

  let { project, compact = false, clickable = false }: Props = $props();

  let isDragging = $state(false);

  // Reactive taskCounts - updates when project prop changes or tasks are modified
  // Initialize with default, then $effect sets proper value and subscribes to updates
  let taskCounts = $state({ total: 0, done: 0, todo: 0 });

  // eslint-disable-next-line momentium/no-unguarded-effect -- external subscription: projectTasksCache pub/sub, re-subscribes on project prop change, returns cleanup
  $effect(() => {
    // Set initial counts (re-runs when project changes since project is referenced)
    taskCounts = getTaskCounts(project);
    // Subscribe to real-time updates
    return subscribeToProjectTaskCounts(project, (counts) => {
      taskCounts = counts;
    });
  });

  function handleDragStart(e: DragEvent) {
    isDragging = true;
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(project.id));
    }
  }

  function handleDragEnd() {
    isDragging = false;
    draggedProject.set(null);
  }

  function togglePriority(e: MouseEvent) {
    e.stopPropagation(); // Don't trigger card click
    router.patch(
      `/projects/${project.id}/toggle_priority`,
      {},
      {
        preserveScroll: true,
      },
    );
  }

  function handleCardClick() {
    if (clickable) {
      openProjectRail(project.id);
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (clickable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      openProjectRail(project.id);
    }
  }
</script>

<div
  class="project-card {categoryColors[project.category]} {project.isTarget ? 'target' : ''}"
  class:dragging={isDragging}
  class:compact
  class:clickable
  draggable={!$viewport.isMobile}
  ondragstart={handleDragStart}
  ondragend={handleDragEnd}
  onclick={handleCardClick}
  onkeydown={handleKeydown}
  role="button"
  tabindex={clickable ? 0 : -1}
>
  <div class="card-content">
    <div class="card-main">
      <!-- Prefix + Title -->
      <div class="title-row">
        {#if project.prefix}
          <span class="prefix">{project.prefix}</span>
        {/if}
        <span class="title">{project.title}</span>
      </div>

      <!-- Meta row -->
      {#if !compact}
        <div class="meta-row">
          <span class="category-icon">{categoryIcons[project.category]}</span>
          {#if project.multiWeek && project.weekIndex && project.totalWeeks}
            <span class="tag span-badge">
              W{project.weekIndex}/{project.totalWeeks}
            </span>
          {/if}
          {#if project.originMonthKey && project.originMonthKey !== project.monthKey}
            <span class="tag origin">
              📅 {project.originMonthKey}
            </span>
          {/if}
          {#if taskCounts.total > 0}
            <span class="tag tasks-tag">
              ✓ {taskCounts.done}/{taskCounts.total}
            </span>
          {/if}
        </div>
      {/if}
    </div>

    <!-- Priority star -->
    <button
      class="priority-btn"
      class:active={project.isPriority}
      onclick={togglePriority}
      title="Toggle priority"
    >
      {project.isPriority ? '★' : '☆'}
    </button>
  </div>

  <!-- Target badge for future months -->
  {#if project.isTarget}
    <div class="target-badge">
      <span>🎯</span>
      <span>Target</span>
    </div>
  {/if}
</div>

<style>
  .project-card {
    overflow: hidden;
  }

  .project-card.compact {
    padding: 0.375rem 0.5rem;
  }

  .project-card.clickable {
    cursor: pointer;
  }

  .project-card.clickable:hover {
    box-shadow:
      0 2px 4px -1px rgba(0, 0, 0, 0.1),
      0 1px 2px -1px rgba(0, 0, 0, 0.06);
  }

  .project-card.clickable:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .card-content {
    display: flex;
    align-items: flex-start;
    gap: 0.375rem;
    min-width: 0;
  }

  .card-main {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  .title-row {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
  }

  .prefix {
    font-size: 0.625rem;
    font-family: ui-monospace, monospace;
    color: #6b7280;
    flex-shrink: 0;
  }

  .title {
    font-weight: 500;
    font-size: 0.75rem;
    color: #1f2937;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  .meta-row {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.25rem;
    font-size: 0.625rem;
    color: #6b7280;
    overflow: hidden;
  }

  .category-icon {
    flex-shrink: 0;
    font-size: 0.625rem;
  }

  .tag {
    padding: 0.125rem 0.25rem;
    background: #e5e7eb;
    border-radius: 0.25rem;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .tag.origin {
    background: #fed7aa;
    color: #c2410c;
  }

  .tag.tasks-tag {
    background: #dbeafe;
    color: #1d4ed8;
  }

  .tag.span-badge {
    background: #dbeafe;
    color: #1d4ed8;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    font-size: 0.5625rem;
    letter-spacing: -0.02em;
  }

  .priority-btn {
    flex-shrink: 0;
    font-size: 0.875rem;
    line-height: 1;
    color: #d1d5db;
    transition: transform 0.15s;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
  }

  .priority-btn:hover {
    transform: scale(1.1);
  }

  .priority-btn.active {
    color: #facc15;
  }

  .target-badge {
    margin-top: 0.25rem;
    font-size: 0.625rem;
    color: #7c3aed;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
</style>
